import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, withModifiers as _withModifiers, withCtx as _withCtx, vShow as _vShow, withDirectives as _withDirectives, Fragment as _Fragment } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "flex flex-col mt-6"
}
const _hoisted_2 = { class: "flex flex-col gap-6" }
const _hoisted_3 = ["innerHTML"]
const _hoisted_4 = {
  key: 0,
  class: "m-auto flex md:hidden"
}
const _hoisted_5 = ["aria-label"]
const _hoisted_6 = ["src", "alt"]
const _hoisted_7 = {
  key: 1,
  class: "m-auto flex md:hidden"
}
const _hoisted_8 = ["aria-label"]
const _hoisted_9 = ["src", "alt"]
const _hoisted_10 = { class: "bg-racq-anti-flash-white rounded-md px-4 py-5" }
const _hoisted_11 = { class: "text-m-bold text-center text-racq-dark-grey" }
const _hoisted_12 = { class: "flex items-center justify-center text-base text-dark-grey-text" }
const _hoisted_13 = { class: "mr-2" }
const _hoisted_14 = ["src", "alt"]
const _hoisted_15 = { class: "flex flex-col gap-4 text-center" }
const _hoisted_16 = ["href"]
const _hoisted_17 = ["href"]
const _hoisted_18 = {
  key: 1,
  class: "text-center py-10 text-gray-500"
}

import { ref, computed, onMounted } from 'vue';
import ModalDialog from '@/components/ModalDialog/ModalDialog.vue';
import MembershipCard from '@/components/MembershipCard/MembershipCard.vue';
import { useAccountStore } from '@/store';
import { downloadGoogleMembershipCard, downloadAppleMembershipCard } from '@/constants/forms-api.const';
import { Field } from '@sitecore-jss/sitecore-jss-vue';
import { LinkFieldValue } from '@sitecore-jss/sitecore-jss-vue/types/components/Link';
import { toFormattedCardNumber } from '@/utils/convert';
import { CardVariant } from '@/enums/membership-card-variant.enum';
import { HOME_PAGE } from '@/constants/urls.const';
import OrderPhysicalCardModal from './OrderPhysicalCardModal.vue';
import { useFeatureTogglesStore } from '@/store/feature-toggles/featureTogglesStore';
import { Link } from '@sitecore-jss/sitecore-jss-vue';

// ---- Types ----
interface MembershipCardModalFields {
  // content / copy
  cardTitle?: Field<string>;
  joinDateLabel?: Field<string>;
  barcodeInfoText?: Field<string>;
  memberCardNumberLabel?: Field<string>;
  copyCardLabel?: Field<string>;
  copiedCardLabel?: Field<string>;
  walletHelpLabel?: Field<string>;
  loadingText?: Field<string>;

  // links
  walletHelpUrl?: string;
  walletHelpCta?: Field<LinkFieldValue>;
  manageCardCta?: Field<LinkFieldValue>;

  // assets
  successIcon?: string;
  copyIcon?: string;

  // temp solution for order membership card PSR-1250
  orderNewCardText?: Field<string>;
  orderNewCardBtn?: Field<string>;
  expectedArrivalText?: Field<string>;
  expectedArrivalBtn?: Field<string>;

  successText?: Field<string>;
  alreadyOrderedTitle?: Field<string>;
  alreadyOrderedText?: Field<string>;
  errorText?: Field<string>;
}

interface Props {
  /** v-model visibility */
  closeOnEsc?: boolean;
  membershipCardModal: any; //MembershipCardModalFields
}


export default /*@__PURE__*/_defineComponent({
  __name: 'MembershipCardModal',
  props: {
    closeOnEsc: { type: Boolean, default: true },
    membershipCardModal: { default: () => ({
    cardTitle: {
      value: 'MEMBERSHIP CARD No.',
    },
    joinDateLabel: {
      value: 'JOIN DATE',
    },
    barcodeInfoText: {
      value: 'Scan this barcode to save at selected RACQ <br> Show & Save retailers and fuel partners.',
    },
    memberCardNumberLabel: { value: 'Membership card number' },
    copyCardLabel: { value: 'Copy' },
    copiedCardLabel: { value: 'Copied' },
    walletHelpLabel: { value: 'How can I add this card to my wallet?' },
    loadingText: { value: 'Loading card details...' },
    walletHelpUrl: HOME_PAGE,
    walletHelpCta: {
      value: {
        href: HOME_PAGE,
      },
    },
    manageCardCta: {
      value: {
        text: 'Manage membership card',
        href: HOME_PAGE,
      },
    },
  }) }
  },
  setup(__props: any, { expose: __expose }) {

const props = __props;

const accountStore = useAccountStore();
const featureToggleStore = useFeatureTogglesStore();
const showModal = ref(false);
const showPhysicalCardModal = ref(false);
const buttonText = ref<string>('Copy');
const isCopied = ref(false);
const isGoogleWalletEnabled = ref(false);
const isAppleWalletEnabled = ref(false);
const memberCardNumber = computed(() => accountStore.identityDetails?.membershipCardNumber || '');

const orderPhysicalCardModal = ref<InstanceType<typeof OrderPhysicalCardModal>>();

const buttonIcon = computed(() =>
  isCopied.value
    ? new URL('@/assets/icons/green-success-icon.svg', import.meta.url).href
    : new URL('@/assets/icons/copy-icon.svg', import.meta.url).href
);

const platform = computed(() => {
  const ua = navigator.userAgent || navigator.vendor;
  const isAndroid = /android/i.test(ua);
  const isApple = /iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream;
  return isAndroid ? 'android' : isApple ? 'apple' : 'unsupported';
});

const wallet = computed(() => {
  return {
    apple: {
      icon: new URL('@/assets/icons/apple_wallet.svg', import.meta.url).href,
      alt: 'Add to Apple Wallet',
    },
    android: {
      icon: new URL('@/assets/icons/google_wallet.svg', import.meta.url).href,
      alt: 'Add to Apple Wallet',
    },
  };
});

onMounted(async () => {
  isAppleWalletEnabled.value = await featureToggleStore.isFeatureToggleEnabled('enableMembershipCardAppleWallet');
  isGoogleWalletEnabled.value = await featureToggleStore.isFeatureToggleEnabled('enableMembershipCardGoogleWallet');
});

__expose({
  showModal: async () => {
    showModal.value = true;
  },
  showOrderPhysicalCardModal: () => {
    showModal.value = false;
    orderPhysicalCardModal.value?.showModal();
  },
});

async function copyToClipboard() {
  if (memberCardNumber?.value) {
    try {
      await navigator.clipboard.writeText(memberCardNumber.value);
      buttonText.value = 'Copied';
      isCopied.value = true;
      setTimeout(() => {
        buttonText.value = 'Copy';
        isCopied.value = false;
      }, 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  }
}

function handleWalletClick(walletPlatform: 'apple' | 'android') {
  if (walletPlatform === 'android') {
    window.location.href = downloadGoogleMembershipCard;
  } else if (walletPlatform === 'apple') {
    alert('Redirecting to Apple Wallet');
    window.location.href = downloadAppleMembershipCard;
  } else {
    alert('Wallet not supported on this device.');
    //send email
  }
}

function openWalletHelp() {
  if (props.membershipCardModal.walletHelpCta?.value?.href)
    window.open(props.membershipCardModal.walletHelpCta?.value?.href, '_blank');
}

/**
 * Temp solution for 26.3-digital ONLY
 * https://jira.racqgroup.local/browse/PSR-1250
 */
function orderPhysicalCard() {
  showModal.value = false;
  showPhysicalCardModal.value = true;
  orderPhysicalCardModal.value?.showModal();
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(ModalDialog, {
      modelValue: showModal.value,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((showModal).value = $event)),
      closeOnEsc: false,
      showXCloseButton: true,
      size: "md"
    }, {
      default: _withCtx(() => [
        (memberCardNumber.value && _ctx.membershipCardModal?.fields)
          ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
              _createVNode(MembershipCard, {
                variant: _unref(CardVariant).BACK,
                cardTitle: _ctx.membershipCardModal?.fields?.cardTitle?.value,
                joinDateLabel: _ctx.membershipCardModal?.fields?.joinDateLabel?.value
              }, null, 8, ["variant", "cardTitle", "joinDateLabel"]),
              _createElementVNode("div", _hoisted_2, [
                _createElementVNode("div", {
                  class: "text-sm text-racq-med-grey text-center mt-3",
                  innerHTML: _ctx.membershipCardModal?.fields.barcodeInfoText?.value
                }, null, 8, _hoisted_3),
                (isAppleWalletEnabled.value && platform.value === 'apple')
                  ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                      _createElementVNode("button", {
                        type: "button",
                        "aria-label": wallet.value.apple.alt,
                        onClick: _cache[0] || (_cache[0] = () => handleWalletClick('apple'))
                      }, [
                        _createElementVNode("img", {
                          src: wallet.value.apple.icon,
                          alt: wallet.value.apple.alt,
                          class: "w-[100%]"
                        }, null, 8, _hoisted_6)
                      ], 8, _hoisted_5)
                    ]))
                  : _createCommentVNode("", true),
                (isGoogleWalletEnabled.value && (platform.value === 'apple' || platform.value === 'android'))
                  ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                      _createElementVNode("button", {
                        type: "button",
                        "aria-label": wallet.value.android.alt,
                        onClick: _cache[1] || (_cache[1] = () => handleWalletClick('android'))
                      }, [
                        _createElementVNode("img", {
                          src: wallet.value.android.icon,
                          alt: wallet.value.android.alt,
                          class: "w-[100%]"
                        }, null, 8, _hoisted_9)
                      ], 8, _hoisted_8)
                    ]))
                  : _createCommentVNode("", true),
                _createElementVNode("div", _hoisted_10, [
                  _createElementVNode("div", _hoisted_11, _toDisplayString(_ctx.membershipCardModal?.fields.memberCardNumberLabel?.value), 1),
                  _createElementVNode("div", _hoisted_12, [
                    _createElementVNode("span", _hoisted_13, _toDisplayString(memberCardNumber.value.replaceAll(' ', '')), 1),
                    _createElementVNode("button", {
                      type: "button",
                      class: "text-m-link font-medium text-racq-blue underline",
                      "aria-live": 'polite',
                      onClick: _withModifiers(copyToClipboard, ["stop","prevent"])
                    }, [
                      _createTextVNode(_toDisplayString(isCopied.value
                  ? _ctx.membershipCardModal?.fields.copiedCardLabel?.value
                  : _ctx.membershipCardModal?.fields.copyCardLabel?.value), 1),
                      _createElementVNode("img", {
                        src: buttonIcon.value,
                        alt: isCopied.value ? 'success' : 'copied',
                        role: "img",
                        class: _normalizeClass(`inline-block ml-1 ${isCopied.value ? 'h-4 w-4' : 'h-[18px] w-[18px]'}`)
                      }, null, 10, _hoisted_14)
                    ])
                  ])
                ]),
                _createElementVNode("div", _hoisted_15, [
                  _createElementVNode("button", {
                    class: "btn btn-secondary btn-md",
                    onClick: orderPhysicalCard
                  }, _toDisplayString(_ctx.membershipCardModal?.fields.manageCardCta?.value.text), 1),
                  _createElementVNode("a", {
                    href: _ctx.membershipCardModal?.fields?.viewCardSettings?.fields?.link?.value?.href,
                    class: "cursor-pointer font-sans"
                  }, _toDisplayString(_ctx.membershipCardModal?.fields?.viewCardSettings?.fields?.link?.value?.text), 9, _hoisted_16),
                  (platform.value === 'unsupported')
                    ? (_openBlock(), _createElementBlock("a", {
                        key: 0,
                        href: _ctx.membershipCardModal?.fields?.howToAddWallet?.fields?.link?.value?.href,
                        class: "cursor-pointer font-sans"
                      }, _toDisplayString(_ctx.membershipCardModal?.fields?.howToAddWallet?.fields?.link?.value?.text), 9, _hoisted_17))
                    : _createCommentVNode("", true),
                  (platform.value == 'unsupported' && _ctx.membershipCardModal?.fields.walletHelpCta?.value?.href)
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 1,
                        "aria-label": "Open Wallet Help",
                        class: "text-racq-blue underline text-base-bold",
                        onClick: openWalletHelp
                      }))
                    : _createCommentVNode("", true)
                ])
              ])
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_18, _toDisplayString(_ctx.membershipCardModal.loadingText?.value), 1))
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _withDirectives(_createVNode(OrderPhysicalCardModal, {
      ref_key: "orderPhysicalCardModal",
      ref: orderPhysicalCardModal,
      membershipCardModal: _ctx.membershipCardModal
    }, null, 8, ["membershipCardModal"]), [
      [_vShow, memberCardNumber.value]
    ])
  ], 64))
}
}

})